import type { JSX } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { estElevationAnnulee, useElevation } from '../../components/useElevation.js';
import { Button, Select } from '@artosera/ui';
import { ajouterExclusion, retirerExclusion } from '../../lib/api.js';
import type { BeneficiaireSaisie, GalerieListe } from '../../types.js';
import commun from '../VueDAigle/commun.module.css';
import { useFormats } from '../VueDAigle/formats.js';
import { useMessageErreur } from '../Finances/commun.js';
import styles from './Comptabilite.module.css';
import { useRelireComptabilite } from './useComptabilite.js';

/** L'ajout d'une exclusion pour un bénéficiaire : sa galerie et son mois de départ. */
function Ajout({
  beneficiaire,
  nom,
  galeries,
  departs,
  surErreur,
  indisponible,
  surEnCours,
}: {
  beneficiaire: BeneficiaireSaisie;
  nom: string;
  galeries: readonly GalerieListe[];
  departs: readonly string[];
  surErreur: (erreur: unknown) => void;
  indisponible: boolean;
  surEnCours: (enCours: boolean) => void;
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const relire = useRelireComptabilite();
  const elevation = useElevation();
  const [galerieId, setGalerieId] = useState<string>('');
  const [depuis, setDepuis] = useState<string>('');
  const [enCours, setEnCours] = useState(false);
  const deja = new Set(beneficiaire.galeriesExclues.map((e) => e.galerieId));
  // Un espace de test n'entre dans aucun chiffre : rien à exclure.
  const proposees = galeries.filter((g) => !g.estTest && !deja.has(g.id));
  const mois = depuis !== '' && departs.includes(depuis) ? depuis : (departs[0] ?? '');

  const exclure = async (): Promise<void> => {
    if (galerieId === '' || mois === '') return;
    setEnCours(true);
    surEnCours(true);
    try {
      await elevation.executer(() =>
        ajouterExclusion(beneficiaire.id, { galerieId, depuisMois: mois }),
      );
      setGalerieId('');
    } catch (erreur) {
      if (!estElevationAnnulee(erreur)) surErreur(erreur);
    } finally {
      setEnCours(false);
      surEnCours(false);
      await relire();
    }
  };

  return (
    <div className={styles.exclusionAjout}>
      <Select
        aria-label={t('comptabilite.exclusions.galerie', { partie: nom })}
        placeholder={t('comptabilite.exclusions.choisirGalerie')}
        options={proposees.map((g) => ({ value: g.id, label: g.nom }))}
        value={galerieId}
        onValueChange={setGalerieId}
        disabled={indisponible || proposees.length === 0}
      />
      <Select
        aria-label={t('comptabilite.exclusions.depuis', { partie: nom })}
        options={departs.map((m) => ({ value: m, label: f.moisLong(m) }))}
        value={mois === '' ? undefined : mois}
        onValueChange={setDepuis}
        disabled={indisponible}
      />
      <Button
        variant="secondary"
        size="sm"
        loading={enCours}
        disabled={indisponible || galerieId === '' || mois === ''}
        onClick={() => void exclure()}
      >
        {t('comptabilite.exclusions.exclure')}
      </Button>
      {elevation.dialogue}
    </div>
  );
}

/**
 * Les galeries exclues de la part de chaque bénéficiaire (direction) : sur les
 * recettes d'une galerie exclue, sa part va au bénéficiaire du reste, les
 * autres gardent la leur. Une exclusion vaut à partir d'un mois ; l'API refuse
 * celle qui toucherait un mois clôturé. Rien n'est calculé ici.
 */
export function Exclusions({
  beneficiaires,
  galeries,
  resteId,
  resteDesigne,
  libelles,
  departs,
}: {
  beneficiaires: readonly BeneficiaireSaisie[];
  galeries: readonly GalerieListe[];
  resteId: string;
  resteDesigne: boolean;
  libelles: ReadonlyMap<string, string>;
  departs: readonly string[];
}): JSX.Element {
  const { t } = useTranslation('console');
  const f = useFormats();
  const message = useMessageErreur('comptabilite');
  const relire = useRelireComptabilite();
  const elevation = useElevation();
  const [erreur, setErreur] = useState<string | null>(null);
  const [retrait, setRetrait] = useState<string | null>(null);
  const [ajoutEnCours, setAjoutEnCours] = useState(false);
  const enCours = retrait !== null || ajoutEnCours;
  const nomGalerie = new Map(galeries.map((g) => [g.id, g.nom]));
  const nom = (id: string): string => libelles.get(id) ?? id;
  const concernes = beneficiaires.filter((b) => b.id !== resteId);

  const retirer = async (beneficiaireId: string, galerieId: string): Promise<void> => {
    setErreur(null);
    setRetrait(`${beneficiaireId}|${galerieId}`);
    try {
      await elevation.executer(() => retirerExclusion(beneficiaireId, galerieId));
    } catch (echec) {
      if (!estElevationAnnulee(echec)) setErreur(message(echec));
    } finally {
      setRetrait(null);
      await relire();
    }
  };

  return (
    <div className={styles.exclusions}>
      <h3 className={styles.exclusionsTitre}>{t('comptabilite.exclusions.titre')}</h3>
      <p className={commun.note}>
        {resteDesigne
          ? t('comptabilite.exclusions.note', { reste: nom(resteId) })
          : t('comptabilite.exclusions.sansReste')}
      </p>
      <ul className={styles.exclusionsListe}>
        {concernes.map((beneficiaire) => (
          <li
            key={beneficiaire.id}
            className={styles.exclusionLigne}
            aria-label={t('comptabilite.exclusions.aria', { partie: nom(beneficiaire.id) })}
          >
            <span className={styles.exclusionNom}>{nom(beneficiaire.id)}</span>
            {beneficiaire.galeriesExclues.length === 0 ? (
              <span className={commun.celluleMuette}>{t('comptabilite.exclusions.aucune')}</span>
            ) : (
              <ul className={styles.exclusionGaleries}>
                {beneficiaire.galeriesExclues.map((exclusion) => (
                  <li key={exclusion.galerieId}>
                    <span>
                      {t('comptabilite.exclusions.galerieDepuis', {
                        galerie:
                          nomGalerie.get(exclusion.galerieId) ??
                          t('comptabilite.exclusions.galerieInconnue'),
                        mois: f.moisAnnee(exclusion.depuisMois),
                      })}
                    </span>
                    <Button
                      variant="ghost"
                      size="sm"
                      disabled={enCours}
                      loading={retrait === `${beneficiaire.id}|${exclusion.galerieId}`}
                      aria-label={t('comptabilite.exclusions.retirerAria', {
                        galerie: nomGalerie.get(exclusion.galerieId) ?? exclusion.galerieId,
                        partie: nom(beneficiaire.id),
                      })}
                      onClick={() => void retirer(beneficiaire.id, exclusion.galerieId)}
                    >
                      {t('comptabilite.exclusions.retirer')}
                    </Button>
                  </li>
                ))}
              </ul>
            )}
            <Ajout
              beneficiaire={beneficiaire}
              nom={nom(beneficiaire.id)}
              galeries={galeries}
              departs={departs}
              surErreur={(echec) => setErreur(message(echec))}
              indisponible={enCours}
              surEnCours={setAjoutEnCours}
            />
          </li>
        ))}
      </ul>
      {erreur ? (
        <p className={styles.erreur} role="alert">
          {erreur}
        </p>
      ) : null}
      {elevation.dialogue}
    </div>
  );
}
